import { trpc, type RouterOutput } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'

export type WorkspaceLifecycleStatus = 'draft' | 'scheduled' | 'published'
export type ScheduledEntry = RouterOutput['workspace']['postStatus']['scheduled'][number]

/** Re-bakes a scheduled post from current workspace content. Implemented by
 *  the preview modal (which owns the projection machinery) and handed to the
 *  header badge via a ref, since the badge lives outside the modal. */
export type UpdateScheduledContentFn = (entry: ScheduledEntry) => Promise<void>

/**
 * Publish state of a workspace, derived from the posts referencing it
 * (workspace.postStatus). One hook so the header badge, the editor locks and
 * the preview-modal strip all agree on what "published" means.
 *
 * Lock rules (frontend guidance only — the hard locks live server-side in
 * assertNotAlreadyPublished / asset.delete):
 * - captions lock per published platform (`publishedPlatforms`)
 * - media locks as soon as anything is published (`mediaLocked`) — media is
 *   shared across platforms, so a per-platform media lock has no coherent
 *   answer.
 */
export const useWorkspacePostStatus = (workspaceId: number | undefined) => {
  const { data } = trpc.workspace.postStatus.useQuery(
    { id: workspaceId ?? 0 },
    { enabled: workspaceId != null },
  )

  const published = data?.published ?? []
  const scheduled = data?.scheduled ?? []

  const status: WorkspaceLifecycleStatus =
    published.length > 0 ? 'published' : scheduled.length > 0 ? 'scheduled' : 'draft'

  const publishedPlatforms = new Set<PlatformType>(published.map((p) => p.platform))
  const scheduledPlatforms = new Set<PlatformType>(scheduled.map((s) => s.platform))

  const getPlatformStatus = (platform: PlatformType): WorkspaceLifecycleStatus =>
    publishedPlatforms.has(platform)
      ? 'published'
      : scheduledPlatforms.has(platform)
        ? 'scheduled'
        : 'draft'

  return {
    status,
    published,
    scheduled,
    publishedPlatforms,
    getPlatformStatus,
    mediaLocked: published.length > 0,
    isCaptionLocked: (platform: PlatformType) => publishedPlatforms.has(platform),
  }
}
